import { useState } from "react";
import { Link } from "react-router-dom";

/**
 * New-Landlord FAQ — static content. Public route, no login. Answers
 * common PA/Philly questions and funnels into the Lease Builder + signup.
 */
const FAQ = [
  {
    q: "How much security deposit can I collect in Pennsylvania?",
    a: `PA law caps the security deposit at <b>2 months' rent</b> during the first year of tenancy and
    <b>1 month's rent</b> after that. Deposits over $100 must be held in a federally insured escrow
    account after the second year — you must give the tenant the name and address of the bank.
    <br/><br/>
    You have <b>30 days</b> after the tenant vacates + provides a forwarding address to return the
    deposit (or send an itemized list of deductions).`,
    tags: ["deposit", "pa"],
  },
  {
    q: "Do I need a rental license in Philadelphia?",
    a: `Yes. Every unit rented in Philadelphia requires a <b>Rental License</b> from the Department
    of Licenses & Inspections (L&I). You'll also need to give tenants a <b>Certificate of Rental
    Suitability</b> at lease signing, plus the "Partners for Good Housing" pamphlet.
    <br/><br/>
    Missing rental license = tenants can withhold rent legally. Get it before you list.`,
    tags: ["license", "philly"],
  },
  {
    q: "How do I screen tenants without breaking Fair Housing laws?",
    a: `Screen every applicant the same way. Legit criteria: credit score, income (usually 2.5–3×
    rent), rental history, and criminal background where allowed. In Philly, <b>you cannot reject
    applicants solely because of housing choice voucher (Section 8)</b> — it's a protected class.
    Also protected: race, color, religion, national origin, sex (including gender identity + sexual
    orientation), familial status, disability, age, and source of income.`,
    tags: ["screening", "fair-housing"],
  },
  {
    q: "How much notice do I need to give to terminate a lease in Philadelphia?",
    a: `Philadelphia has a <b>Good Cause Eviction Ordinance</b>. For non-renewal or termination
    without cause, landlords generally must give <b>30 days notice</b> for month-to-month tenancies
    and <b>60 days</b> for tenants who've been there over a year. Fixed-term leases end on the date
    stated, but you should still send a renewal/non-renewal notice.`,
    tags: ["eviction", "notice", "philly"],
  },
  {
    q: "Can I raise the rent mid-lease?",
    a: `<b>Fixed-term lease:</b> No — rent is locked until renewal.
    <br/>
    <b>Month-to-month:</b> Yes, with proper written notice — usually the same length as the notice
    to terminate (30 days minimum in PA). Philadelphia does NOT have rent control, but any increase
    must be non-retaliatory and non-discriminatory.`,
    tags: ["rent", "philly"],
  },
  {
    q: "Who pays for utilities, and can I include them in rent?",
    a: `Anything the lease says. Common setups: tenant pays gas + electric + internet, landlord pays
    water/sewer + trash (since Philly water is landlord-billed). If you include utilities in rent,
    you MUST separately meter or estimate fairly — you can't profit off resold utilities.`,
    tags: ["utilities"],
  },
  {
    q: "What repairs am I actually responsible for?",
    a: `PA follows the <b>Implied Warranty of Habitability</b>. You must provide: safe structure,
    working plumbing (hot + cold water, working toilet), heating in cold months, functioning electric,
    weather-tight roof + windows, working locks, no infestations. Tenant handles: normal cleaning,
    minor damage they cause, lightbulbs, basic upkeep.
    <br/><br/>
    Emergency repairs (no heat, no water, sewage backup) require <b>immediate</b> response — tenants
    can legally withhold rent or "repair and deduct" if you ignore them.`,
    tags: ["habitability", "repairs"],
  },
  {
    q: "How much notice do I have to give before entering the unit?",
    a: `PA doesn't have a statutory notice requirement, but industry standard (and what most leases
    say) is <b>24 hours written notice</b> for inspections, repairs, or showings — except in
    emergencies (fire, flood, gas leak). Respect this or you'll lose tenants (and end up in court).`,
    tags: ["access"],
  },
  {
    q: "What's the eviction process in Philly, start to finish?",
    a: `1. Send a proper written notice (10-day notice for non-payment, 15-day for lease breach, or
    30-day if no lease).
    <br/>
    2. File a Landlord/Tenant Complaint in Municipal Court.
    <br/>
    3. Serve the tenant.
    <br/>
    4. Hearing (usually 15–30 days out).
    <br/>
    5. If you win, apply for a Writ of Possession.
    <br/>
    6. Sheriff schedules the lockout.
    <br/><br/>
    Total time: <b>6 weeks minimum</b>, often 3+ months if tenant contests. <b>Never</b> change
    locks, cut utilities, or remove tenant belongings yourself — that's illegal self-help eviction
    in every state, and you'll owe the tenant damages.`,
    tags: ["eviction", "philly"],
  },
  {
    q: "Should I hire a property manager, or do it myself?",
    a: `Rough math: property management usually costs 8–12% of monthly rent + a placement fee.
    <br/><br/>
    <b>Manage yourself when:</b> you own 1–3 units, live nearby, have time to answer calls after
    hours, and enjoy the operational side.
    <br/><br/>
    <b>Hire a PM (or use Autopilot) when:</b> you own 4+ units, live out-of-state, have a demanding
    day job, or don't want to be woken up by a burst pipe at 2 AM. Automation tools like Field Radar
    fall in between — you keep control, but the ops (dispatch, tenant chat, rent collection) is
    handled without a human PM.`,
    tags: ["self-manage", "pm"],
  },
  {
    q: "Do I need landlord insurance?",
    a: `<b>Yes.</b> A regular homeowner's policy usually excludes rental use. You need a Dwelling
    Fire policy (DP-1 / DP-3) or a Landlord Package policy — those cover the structure, loss of
    rent, and liability. Ballpark: $400–$1,200/yr per unit in Philly. Also require your <b>tenant
    to carry renters insurance</b> — put it in the lease.`,
    tags: ["insurance"],
  },
  {
    q: "Can I write off rental income losses on taxes?",
    a: `Rental properties are taxed as passive income. You can deduct: mortgage interest, property
    tax, insurance, repairs, depreciation (straight-line over 27.5 years for residential), travel to
    the property, professional fees. If total deductions exceed income and your AGI is under
    $150K, you can generally deduct up to $25K in passive losses against ordinary income.
    <b>Talk to a CPA</b> — this section is not tax advice.`,
    tags: ["taxes"],
  },
  {
    q: "What's a Certificate of Occupancy vs. a Rental License?",
    a: `In Philly:
    <br/>
    <b>Certificate of Occupancy</b> = says the building is legally habitable and safe for its zoning
    use. Required for new buildings + major renovations.
    <br/>
    <b>Rental License</b> = permission to operate as a landlord. Required annually. Failing to have
    one = tenant doesn't owe you rent until you get one.
    <br/><br/>
    Both are required to rent legally.`,
    tags: ["license", "philly"],
  },
  {
    q: "How do I handle a tenant with an emotional support animal?",
    a: `ESAs are protected under the Fair Housing Act. You cannot refuse them even with a strict
    "no pets" policy, and you cannot charge a pet deposit or pet rent. You <b>can</b> ask for
    documentation from a licensed mental health provider, and the tenant is still responsible for
    any damage the animal causes. Service animals (trained under ADA) have even stronger protection.`,
    tags: ["pets", "fair-housing"],
  },
  {
    q: "What should I do with a difficult tenant instead of eviction?",
    a: `Eviction is slow and expensive. Alternatives:
    <br/>
    • <b>Cash for keys:</b> offer $500–$2,000 for the tenant to voluntarily move out by a set date.
    Way cheaper than 3 months of lost rent + legal fees.
    <br/>
    • <b>Mediation:</b> Philly has free tenant-landlord mediation programs.
    <br/>
    • <b>Payment plan:</b> If it's a money issue, a written plan is almost always cheaper than court.
    <br/>
    • <b>Non-renewal:</b> If they're month-to-month, just don't renew.`,
    tags: ["difficult-tenant", "eviction"],
  },
];

export default function LandlordFaq() {
  const [q, setQ] = useState("");
  const [openIx, setOpenIx] = useState(new Set([0]));

  const filtered = q.trim()
    ? FAQ.filter(f =>
        f.q.toLowerCase().includes(q.toLowerCase()) ||
        f.a.toLowerCase().includes(q.toLowerCase()) ||
        f.tags.some(t => t.includes(q.toLowerCase())))
    : FAQ;

  const toggle = (i) => setOpenIx(prev => {
    const next = new Set(prev);
    next.has(i) ? next.delete(i) : next.add(i);
    return next;
  });

  return (
    <div className="min-h-screen bg-slate-50">
      <header className="bg-[#0B1F3A] text-white py-8">
        <div className="max-w-4xl mx-auto px-4">
          <Link to="/landlord" className="text-[10px] uppercase tracking-widest text-cyan-300 hover:underline font-black">
            ← Back to Landlord Autopilot
          </Link>
          <h1 className="font-display font-black text-3xl sm:text-4xl mt-2">
            New-Landlord FAQ
          </h1>
          <p className="text-sm text-slate-300 mt-2 max-w-2xl">
            The 15 questions every new PA landlord asks — plain English, no filler. Written with
            Philadelphia in mind. Not legal advice.
          </p>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 sm:p-6">
        {/* Search */}
        <div className="mb-4">
          <input value={q} onChange={e => setQ(e.target.value)}
            placeholder="Search FAQs (e.g. deposit, eviction, screening)"
            data-testid="faq-search"
            className="w-full h-11 px-4 border-2 border-slate-300 focus:border-cyan-500 outline-none rounded-lg text-sm"/>
        </div>

        {filtered.length === 0 && (
          <div className="p-6 text-center text-sm text-slate-400 italic">
            No matches. Try "deposit", "license", or "eviction".
          </div>
        )}

        <div className="space-y-2">
          {filtered.map((item) => {
            const ix = FAQ.indexOf(item);
            const open = openIx.has(ix);
            return (
              <div key={ix} className="bg-white border-2 border-slate-200 rounded-lg overflow-hidden"
                data-testid={`faq-item-${ix}`}>
                <button onClick={() => toggle(ix)} type="button"
                  className="w-full flex items-center justify-between gap-3 p-4 hover:bg-slate-50 text-left"
                  data-testid={`faq-toggle-${ix}`}>
                  <span className="font-black text-sm text-[#0B1F3A]">{item.q}</span>
                  <span className="text-slate-400 flex-shrink-0">{open ? "−" : "+"}</span>
                </button>
                {open && (
                  <div className="px-4 pb-4 pt-1 text-sm text-slate-700 leading-relaxed"
                    dangerouslySetInnerHTML={{ __html: item.a }}/>
                )}
              </div>
            );
          })}
        </div>

        {/* Upsell */}
        <div className="mt-8 p-5 bg-white border-2 border-cyan-500 rounded-xl shadow-[6px_6px_0px_#00C4CC]">
          <div className="font-display font-black text-xl text-[#0B1F3A]">Ready to make it real?</div>
          <p className="text-sm text-slate-600 mt-2">
            Build a PA-focused lease in 2 minutes, or sign up and let Field Radar handle the whole
            landlord life for you — rent collection, repair dispatch, tenant chat.
          </p>
          <div className="mt-3 flex flex-wrap gap-2">
            <Link to="/lease-builder"
              data-testid="faq-cta-lease"
              className="h-10 px-4 inline-flex items-center border-2 border-[#0B1F3A] bg-white hover:bg-slate-100 text-slate-900 font-black uppercase tracking-widest text-xs">
              📄 Build a Lease
            </Link>
            <Link to="/landlord/signup"
              data-testid="faq-cta-signup"
              className="h-10 px-4 inline-flex items-center border-2 border-[#0B1F3A] bg-[#00C4CC] hover:bg-[#00a8af] text-[#0B1F3A] hover:text-white font-black uppercase tracking-widest text-xs">
              Start Free Trial →
            </Link>
          </div>
        </div>

        <p className="text-[10px] text-slate-400 text-center mt-8 mb-4">
          These answers are educational, not legal advice. Consult a licensed PA attorney for anything
          material.
        </p>
      </main>
    </div>
  );
}
